import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Box, Card, TextField, Button, Typography, Alert, Divider, ThemeProvider, IconButton, InputAdornment } from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { Eye, EyeOff } from 'lucide-react';
import { useSignIn, useSignInWithGoogle } from '@diggens/shared-hooks';
import { lightTheme } from '@diggens/shared-theme';
import { ICON_SIZES, ICON_STROKE } from '../constants/icons';

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const signIn = useSignIn();
  const signInWithGoogle = useSignInWithGoogle();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      await signIn.mutateAsync({ email, password });
      navigate('/home');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to sign in');
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle.mutateAsync();
      // User will be redirected to Google, then back to /dashboard
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to sign in with Google');
    }
  };

  return (
    <ThemeProvider theme={lightTheme}>
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="grey.100">
        <Card sx={{ p: 4, maxWidth: 400, width: '100%' }}>
          <Typography variant="h4" gutterBottom>
            Sign In
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField fullWidth label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} margin="normal" required />
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                      {showPassword ? <EyeOff size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} /> : <Eye size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button fullWidth variant="contained" type="submit" disabled={signIn.isPending} sx={{ mt: 2 }}>
              {signIn.isPending ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <Divider sx={{ my: 2 }}>OR</Divider>

          <Button fullWidth variant="outlined" onClick={handleGoogleSignIn} disabled={signInWithGoogle.isPending} startIcon={<GoogleIcon />}>
            Sign in with Google
          </Button>

          <Typography sx={{ mt: 2 }}>
            Don't have an account? <Link to="/signup">Sign up</Link>
          </Typography>
        </Card>
      </Box>
    </ThemeProvider>
  );
}
